import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Smile, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import EmojiPickerReact, { EmojiClickData, Theme } from 'emoji-picker-react';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥'];

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  className?: string;
  variant?: 'full' | 'quick';
  side?: 'top' | 'right' | 'bottom' | 'left';
  isGlobalDarkMode: boolean;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onSelect, className, variant = 'full', side = 'bottom', isGlobalDarkMode }) => {
  const [open, setOpen] = useState(false);
  const [showFullPicker, setShowFullPicker] = useState(false);

  // Reset to quick view when closed
  useEffect(() => {
    if (!open) {
      setShowFullPicker(false);
    }
  }, [open]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    onSelect(emojiData.emoji);
    setOpen(false);
  };

  const handleQuickSelect = (emoji: string) => {
    onSelect(emoji);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-7 w-7 transition-colors", isGlobalDarkMode ? "text-muted-foreground hover:text-foreground hover:bg-accent" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100", className)}
        >
          <Smile className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      {variant === 'quick' && !showFullPicker ? (
        <PopoverContent
          className="w-auto p-0 border-none shadow-none bg-transparent z-[200]"
          align="start"
          side={side}
          sideOffset={8}
        >
          <div className={cn(
            "flex items-center gap-1 p-2 border rounded-full shadow-xl animate-in fade-in zoom-in duration-200",
            isGlobalDarkMode ? "bg-card border-border shadow-black/40" : "bg-popover border-border shadow-slate-200/50"
          )}>
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleQuickSelect(emoji)}
                className={cn(
                  "w-8 h-8 flex items-center justify-center text-xl rounded-full transition-all hover:scale-125 active:scale-90",
                  isGlobalDarkMode ? "hover:bg-accent" : "hover:bg-muted"
                )}
              >
                {emoji}
              </button>
            ))}
            <div className={cn("w-px h-6 mx-1", isGlobalDarkMode ? "bg-border" : "bg-border")} />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowFullPicker(true)}
              className={cn("w-8 h-8 rounded-full transition-colors", isGlobalDarkMode ? "text-muted-foreground hover:text-foreground hover:bg-accent" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100")}
              title="More emojis"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </PopoverContent>
      ) : (
        <PopoverContent
          className="w-full p-0 border-none shadow-xl bg-transparent z-[200]"
          align="start"
          side={side}
          sideOffset={8}
        >
          <EmojiPickerReact
            onEmojiClick={handleEmojiClick}
            theme={isGlobalDarkMode ? Theme.DARK : Theme.LIGHT}
            width={300}
            height={400}
            lazyLoadEmojis={true}
            searchDisabled={false}
            skinTonesDisabled={false}
          />
        </PopoverContent>
      )}
    </Popover>
  );
};

export default EmojiPicker;
